import { Controller } from "@hotwired/stimulus"
import { startIcons } from "../import_lucid"

export default class extends Controller {
  static targets = ["button", "icon"]

  connect() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    this._supported = !!SR

    if (!this._supported) {
      this.element.classList.add('hidden')
      return
    }

    this._listening = false
    this._finalTranscript = ''
  }

  // The composer this button belongs to. Resolving a single global id only ever worked in the
  // main chat — the in-page panel and the platform panel are different forms, and the platform
  // one has no id at all. Look inside our own form first, fall back to the old id.
  get input() {
    return this.element.closest("form")?.querySelector("textarea[name='content']") ||
           document.getElementById("chat-input");
  }

  toggle() {
    if (!this._supported) return

    if (this._listening) {
      this._stop()
    } else {
      this._start()
    }
  }

  async _start() {
    // First request mic permission via getUserMedia (works in all browsers)
    // This triggers the Chrome permission popup and unblocks SpeechRecognition
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // Stop the stream immediately — we just needed the permission
      stream.getTracks().forEach(t => t.stop())
    } catch (e) {
      console.warn('Microphone access denied:', e.message)
      this.buttonTarget.title = 'Microphone access denied'
      return
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    const recognition = new SR()
    recognition.lang = 'en-IN'
    recognition.interimResults = true
    recognition.continuous = false
    recognition.maxAlternatives = 1

    this._finalTranscript = ''

    // Append to existing text if any
    const input = this.input
    if (input && input.value.trim()) {
      this._finalTranscript = input.value.trim() + ' '
    }

    recognition.onresult = (event) => {
      let interim = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          this._finalTranscript += transcript
        } else {
          interim += transcript
        }
      }
      const el = this.input
      if (el) {
        el.value = this._finalTranscript + interim
        el.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }

    recognition.onend = () => {
      this._listening = false
      this._updateUI(false)
      const el = this.input
      if (el && this._finalTranscript.trim()) {
        el.focus()
        el.selectionStart = el.selectionEnd = el.value.length
      }
    }

    recognition.onerror = (event) => {
      console.warn('Speech recognition error:', event.error)
      this._listening = false
      this._updateUI(false)
    }

    try {
      recognition.start()
      this._recognition = recognition
      this._listening = true
      this._updateUI(true)
    } catch (e) {
      console.warn('Speech recognition failed to start:', e)
    }
  }

  _stop() {
    if (this._recognition) {
      this._recognition.stop()
    }
  }

  _updateUI(active) {
    const btn = this.buttonTarget
    const icon = this.iconTarget

    if (active) {
      btn.classList.add('laz-voice-active')
      icon.setAttribute('data-lucide', 'mic-off')
      btn.title = 'Stop listening'
    } else {
      btn.classList.remove('laz-voice-active')
      icon.setAttribute('data-lucide', 'mic')
      btn.title = 'Voice input'
    }

    // Re-render lucide icon
    startIcons(this.element)
  }

  disconnect() {
    if (this._recognition && this._listening) {
      this._recognition.stop()
    }
  }
}
